import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Calendar, Building2, Users, Globe, ArrowRight, MapPin, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [featured, setFeatured] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    axios.get(`${API}/stats`).then(r => setStats(r.data)).catch(() => {});
    axios.get(`${API}/exhibitions?limit=12`).then(r => {
      const items = r.data.items || [];
      setFeatured(items.slice(0, 4));
      setUpcoming(items.slice(4, 12));
    }).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/exhibitions?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Navbar */}
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-12 flex items-center justify-between">
          <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => navigate("/")}>
            <Calendar className="h-5 w-5 text-blue-600" />
            <span className="text-base font-bold tracking-tight">ExpoPulse <span className="text-blue-600">Events</span></span>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <button onClick={() => navigate("/exhibitions")} className="text-gray-600 hover:text-gray-900 transition">Exhibitions</button>
            <button onClick={() => navigate("/organizers")} className="text-gray-600 hover:text-gray-900 transition">Organizers</button>
          </div>
        </div>
      </nav>

      {/* Hero — compact */}
      <section className="bg-gradient-to-b from-blue-50 to-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-3 leading-tight">
            Global Trade Show Directory
          </h1>
          <p className="text-base text-gray-500 mb-6 max-w-xl mx-auto">
            {stats?.exhibitions?.toLocaleString() || "13,000+"} exhibitions · {stats?.organizers || "530"} organizers · {stats?.countries || "170"} countries
          </p>

          <form onSubmit={handleSearch} className="max-w-lg mx-auto mb-8">
            <div className="flex bg-white border border-gray-300 rounded-md overflow-hidden shadow-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition">
              <div className="flex items-center pl-3">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search exhibitions, industries, cities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 px-3 py-2.5 text-sm outline-none"
              />
              <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 text-sm font-medium transition">
                Search
              </button>
            </div>
          </form>

          {/* Stats inline */}
          {stats && (
            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
              <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-blue-500" /> {stats.exhibitions?.toLocaleString()} exhibitions</span>
              <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5 text-blue-500" /> {stats.organizers} organizers</span>
              <span className="flex items-center gap-1"><Globe className="h-3.5 w-3.5 text-blue-500" /> {stats.countries} countries</span>
              <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5 text-blue-500" /> {stats.exhibitors?.toLocaleString()} exhibitors</span>
            </div>
          )}
        </div>
      </section>

      {/* Featured — 4 columns, compact cards */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Featured Exhibitions</h2>
          <button onClick={() => navigate("/exhibitions")}
            className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-xs font-medium">
            View all <ArrowRight className="h-3 w-3" />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {featured.map((ex) => (
            <ExhibitionCard key={ex._id} ex={ex} navigate={navigate} />
          ))}
        </div>
      </section>

      {/* More exhibitions — compact list */}
      <section className="max-w-7xl mx-auto px-4 pb-8">
        <h2 className="text-lg font-semibold mb-4">Upcoming Events</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {upcoming.map((ex) => (
            <ExhibitionCard key={ex._id} ex={ex} navigate={navigate} />
          ))}
        </div>
      </section>

      {/* CTA — compact */}
      <section className="max-w-7xl mx-auto px-4 pb-10">
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold mb-1">Capture leads at trade shows</h3>
            <p className="text-sm text-gray-500">Scan business cards, record voice notes, manage contacts with ExpoPulse app.</p>
          </div>
          <a href="https://go.expopulse.app" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition whitespace-nowrap">
            Try ExpoPulse <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 text-center text-xs text-gray-400">
          © 2026 ExpoPulse Events · Data powered by ExpoWorld
        </div>
      </footer>
    </div>
  );
}

function ExhibitionCard({ ex, navigate }) {
  return (
    <div
      onClick={() => navigate(`/exhibition/${ex._id}`)}
      className="bg-white border border-gray-200 rounded-md p-3 hover:border-blue-300 hover:shadow-sm transition cursor-pointer group"
    >
      <div className="flex items-start gap-3">
        {ex.image_url && (
          <img src={ex.image_url} alt="" className="w-10 h-10 rounded object-cover bg-gray-100 shrink-0"
            onError={(e) => e.target.style.display='none'} />
        )}
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-medium leading-tight group-hover:text-blue-600 transition line-clamp-2">{ex.name}</h3>
          <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{[ex.city, ex.country].filter(Boolean).join(", ")}</span>
          </div>
        </div>
      </div>
      {ex.start_date && (
        <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
          <Calendar className="h-3 w-3 shrink-0" />
          {ex.start_date}{ex.end_date ? ` — ${ex.end_date}` : ""}
        </div>
      )}
      {ex.industry && (
        <div className="mt-2 flex flex-wrap gap-1">
          {ex.industry.split(",").slice(0, 2).map((tag, i) => (
            <span key={i} className="inline-block bg-gray-100 text-gray-600 text-[10px] px-1.5 py-0.5 rounded">
              {tag.trim()}
            </span>
          ))}
        </div>
      )}
      {ex.organizer_name && (
        <div className="text-[10px] text-gray-400 mt-1.5 truncate">by {ex.organizer_name}</div>
      )}
    </div>
  );
}
